import { Module } from 'vuex'
import { RootStoreState } from '@/store/root/root-store.model'
import { VtStore } from '@/store/vt-store'
import { DashboardModuleUiComponent, DashboardStoreState } from '@/modules/dashboard-module/store/dashboard-store.model'
import { DashboardModuleArea, DashboardModuleLayoutWidget } from '@/modules/dashboard-module/shared/models/dashboard-module.model'
import { GridItemMovedEvent } from 'vue-grid-layout'
import { cloneDeep } from 'lodash'

const initialDashboardStoreState =
    (): DashboardStoreState => ({
        uiComponent: {
            id: null,
            name: 'uicomponent.dashboard.new'
        },
        allWidgets: [],
        pendingArea: [],
        chartArea: [],
        interestingDataArea: [],
        utilitiesArea: []
    })

const module: Module<DashboardStoreState, RootStoreState> = {
    namespaced: true,
    state: initialDashboardStoreState,
    getters: {
        uiComponent: (state): DashboardModuleUiComponent => state.uiComponent,
        allWidgets: (state): DashboardModuleLayoutWidget[] => state.allWidgets,
        pendingArea: (state): DashboardModuleLayoutWidget[] => state.pendingArea,
        chartArea: (state): DashboardModuleLayoutWidget[] => state.chartArea,
        interestingDataArea: (state): DashboardModuleLayoutWidget[] => state.interestingDataArea,
        utilitiesArea: (state): DashboardModuleLayoutWidget[] => state.utilitiesArea
    },
    mutations: {
        setIdUIComponent: (state: DashboardStoreState, payload: number): void => { state.uiComponent.id = payload },
        setAllWidgets: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget[]): void => { state.allWidgets = payload },
        addWidgetToPendingArea: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget): void => { state.pendingArea.push(payload) },
        addWidgetToChartArea: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget): void => { state.chartArea.push(payload) },
        addWidgetToInterestingDataArea: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget): void => { state.interestingDataArea.push(payload) },
        addWidgetToUtilitiesArea: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget): void => { state.utilitiesArea.push(payload) },

        updatedPendingAreaWidgets: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget[]): void => { state.pendingArea = payload },
        updatedChartsAreaWidgets: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget[]): void => { state.chartArea = payload },
        updatedInterestAreaAreaWidgets: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget[]): void => { state.interestingDataArea = payload },
        updatedUtilitiesAreaWidgets: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget[]): void => { state.utilitiesArea = payload },

        removeWidgetToPendingArea: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget): void => {
            state.pendingArea =
                state.pendingArea.reduce<DashboardModuleLayoutWidget[]>((aggr, item) => {
                    if (item.id !== payload.id) {
                        aggr.push(item)
                    }
                    return aggr
                }, [])
        },
        removeWidgetToChartArea: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget): void => {
            state.chartArea =
                state.chartArea.reduce<DashboardModuleLayoutWidget[]>((aggr, item) => {
                    if (item.id !== payload.id) {
                        aggr.push(item)
                    }
                    return aggr
                }, [])
        },
        removeWidgetToInterestingDataArea: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget): void => {
            state.interestingDataArea =
                state.interestingDataArea.reduce<DashboardModuleLayoutWidget[]>((aggr, item) => {
                    if (item.id !== payload.id) {
                        aggr.push(item)
                    }
                    return aggr
                }, [])
        },
        removeWidgetToUtilitiesArea: (state: DashboardStoreState, payload: DashboardModuleLayoutWidget): void => {
            state.utilitiesArea =
                state.utilitiesArea.reduce<DashboardModuleLayoutWidget[]>((aggr, item) => {
                    if (item.id !== payload.id) {
                        aggr.push(item)
                    }
                    return aggr
                }, [])
        }
    },
    actions: {
        addWidget: ({ commit }, payload: DashboardModuleLayoutWidget): DashboardModuleLayoutWidget => {
            return payload
        },
        removeWidget: ({ commit }, payload: DashboardModuleLayoutWidget): DashboardModuleLayoutWidget => {
            return payload
        },
        addWidgetToArea: ({ commit }, payload: DashboardModuleLayoutWidget): void => {
            switch (payload.area) {
            case DashboardModuleArea.PENDING_ELEMENTS:
                commit('addWidgetToPendingArea', payload)
                break
            case DashboardModuleArea.CHARTS:
                commit('addWidgetToChartArea', payload)
                break
            case DashboardModuleArea.INTEREST_DATA:
                commit('addWidgetToInterestingDataArea', payload)
                break
            case DashboardModuleArea.UTILITIES:
                commit('addWidgetToUtilitiesArea', payload)
                break
            }
        },
        removeWidgetFromArea: ({ commit }, payload: DashboardModuleLayoutWidget): void => {
            switch (payload.area) {
            case DashboardModuleArea.PENDING_ELEMENTS:
                commit('removeWidgetToPendingArea', payload)
                break
            case DashboardModuleArea.CHARTS:
                commit('removeWidgetToChartArea', payload)
                break
            case DashboardModuleArea.INTEREST_DATA:
                commit('removeWidgetToInterestingDataArea', payload)
                break
            case DashboardModuleArea.UTILITIES:
                commit('removeWidgetToUtilitiesArea', payload)
                break
            }
        },
        setAllWidgets: ({ commit }, payload: DashboardModuleLayoutWidget[]): void => {
            commit('setAllWidgets', payload)
        },
        setVisibleWidget: ({ commit, state }, payload: DashboardModuleLayoutWidget): void => {
            const updatedWidgets = changeVisibilityWidget([...state.allWidgets], payload.id, false)
            commit('setAllWidgets', updatedWidgets)
        },
        setHideWidget: ({ commit, state }, payload: DashboardModuleLayoutWidget): void => {
            const updatedWidgets = changeVisibilityWidget([...state.allWidgets], payload.id, true)
            commit('setAllWidgets', updatedWidgets)
        },
        setIdUIComponent: ({ commit }, payload: number): void => {
            commit('setIdUIComponent', payload)
        },
        setCoordinatesWhenMoved: ({ commit, state }, payload: GridItemMovedEvent): void => {
            const widget = state.allWidgets.find((item: DashboardModuleLayoutWidget) => item.id === payload.i)
            switch (widget?.area) {
            case DashboardModuleArea.PENDING_ELEMENTS:
                const updatedPendingAreaWidgets = updateCoordinatesArea(state.pendingArea, payload)
                commit('updatedPendingAreaWidgets', updatedPendingAreaWidgets)
                break
            case DashboardModuleArea.CHARTS:
                const updatedChartsAreaWidgets = updateCoordinatesArea(state.chartArea, payload)
                commit('updatedChartsAreaWidgets', updatedChartsAreaWidgets)
                break
            case DashboardModuleArea.INTEREST_DATA:
                const updatedInterestAreaAreaWidgets = updateCoordinatesArea(state.interestingDataArea, payload)
                commit('updatedInterestAreaAreaWidgets', updatedInterestAreaAreaWidgets)
                break
            case DashboardModuleArea.UTILITIES:
                const updatedUtilitiesAreaWidgets = updateCoordinatesArea(state.utilitiesArea, payload)
                commit('updatedUtilitiesAreaWidgets', updatedUtilitiesAreaWidgets)
                break
            }
        },
        addWidgetAndSetVisible: async ({ dispatch }, payload: DashboardModuleLayoutWidget): Promise<void> => {
            await dispatch('setVisibleWidget', payload)
            await dispatch('addWidget', payload)
        },
        removeWidgetAndSetVisible: async ({ dispatch }, payload: DashboardModuleLayoutWidget): Promise<void> => {
            await dispatch('setHideWidget', payload)
            await dispatch('removeWidget', payload)
        }
    }
}
export const dashboardStore = new VtStore<DashboardStoreState>('dashboard', module)

const changeVisibilityWidget =
    (state: DashboardModuleLayoutWidget[], idWidget: string, visibility: boolean): DashboardModuleLayoutWidget[] => {
        return state.reduce<DashboardModuleLayoutWidget[]>((aggr, item) => {
            const newItem = cloneDeep(item)
            if (newItem.id === idWidget) {
                newItem.hidden = visibility
            }
            aggr.push(newItem)
            return aggr
        }, [])
    }

const updateCoordinatesArea =
    (state: DashboardModuleLayoutWidget[], widgetCoordinates: GridItemMovedEvent): DashboardModuleLayoutWidget[] => {
        return state.reduce<DashboardModuleLayoutWidget[]>((aggr, item) => {
            const newItem = cloneDeep(item)
            if (newItem.id === widgetCoordinates.i) {
                if (newItem.coordinates) {
                    newItem.coordinates.x = widgetCoordinates.newX
                    newItem.coordinates.y = widgetCoordinates.newY
                }
            }
            aggr.push(newItem)
            return aggr
        }, [])
    }
